import axios from 'axios';
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Método: Autenticación del usuario
export const authenticateUser = async (usuario, password) => {
  try {    
    const response = await axios.post(`${API_BASE_URL}/Auth`, {       
      usuario,
      password,
    });

    if (response.data.message === 'OK') {
      const token = response.data.token;
      localStorage.setItem('authToken', token);
      return token;
    } else {
      throw new Error('Autenticación fallida');
    }
  } catch (error) {
    console.error('Error durante la autenticación:', error.response ? error.response.data : error.message);
    throw error;
  }
};

// Método: Información del paciente
export const getPacienteInfo = async (rut) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await axios.get(`${API_BASE_URL}/paciente/infoPaciente/${rut}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener la información del paciente:', error.response ? error.response.data : error.message);
    throw error;
  }
};

// Método: obtener los agendamientos del paciente
export const getAgendamientosPaciente = async (rut) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await axios.get(`${API_BASE_URL}/agenda/agendamientosPaciente/${rut}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Filtrar los agendamientos para devolver solo aquellos que no estén confirmados
    if (response.data.codigo === 200) {
      const agendamientosNoConfirmados = response.data.datos.filter(
        (agendamiento) => agendamiento.CONFIRMADO === null
      );
      return { ...response.data, datos: agendamientosNoConfirmados };
    }

    return response.data;
  } catch (error) {
    console.error('Error al obtener los agendamientos del paciente:', error.response ? error.response.data : error.message);
    throw error;
  }
};

// Método: Cambiar el estado de un agendamiento "ANULAR" o "CONFIRMAR"
export const cambiarEstadoAgendamiento = async (ageId, estado) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await axios.put(
      `${API_BASE_URL}/agenda/cambioEstado`,
      {
        age_id: ageId,
        estado: estado,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data; 
  } catch (error) {
    throw error;
  }
};

// Método: obtener Horarios Vigentes
export const getHorariosVigente = async (sucId, odontRut = null, token, clasSillon = "Web2", clasifHorario) => {
  if (!sucId || !clasifHorario) {
      throw new Error('Parámetros inválidos: sucId o clasifHorario están vacíos');
  }

  try {
      const params = {
          suc_id: sucId,
          clas_sillon: clasSillon,
          clasif_horario: clasifHorario
      };

      // Solo agregar `odont_rut` si está presente
      if (odontRut) {
          params.odont_rut = odontRut;
      }

      /* console.log("Consultando horarios con parámetros:", params); */

      const response = await axios.get(`${API_BASE_URL}/agenda/horariosVigente`, {
          params,
          headers: {
              Authorization: `Bearer ${token}`,
          },
      });

      return response.data;
  } catch (error) {
      console.error('Error al obtener los horarios vigentes:', error.response ? error.response.data : error.message);
      throw error;
  }
};

// Método: Revalidar el token de autenticación
// Este método se puede usar para refrescar el token antes de que expire
export const revalidateToken = async () => {
  try {
    // Revalidar el token
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      // datos necesarios para revalidar
    });
    const newToken = response.data.token;
    localStorage.setItem('authToken', newToken);
    return true;
  } catch (error) {
    console.error('Error al revalidar el token:', error);
    return false;
  }
}; 

// Método: Obtener la lista de agendamientos filtrados por sucursal y fechas
// Este método se puede usar para obtener agendamientos en un rango de fechas específico
export const listaAgendamientosFiltrados = async (token, sucId, fechaDesde, fechaHasta) => {
  try {
      /* console.log("Ejecutando listaAgendamientosFiltrados con parámetros:", {
          sucursalId: sucId,
          fechaDesde,
          fechaHasta
      }); */

      const response = await axios.get(`${API_BASE_URL}/agenda/listaAgendamientosFiltrados`, {
          params: {
              suc_id: sucId,
              fecha_desde: fechaDesde,
              fecha_hasta: fechaHasta,
          },
          headers: { Authorization: `Bearer ${token}` }
      });

      /* console.log(`Agendamientos obtenidos correctamente:`, response.data); */
      return response.data;
  } catch (error) {
      console.error(`Error en listaAgendamientosFiltrados:`, error);
      throw error;
  }
};

// Método: Obtener la lista de odontólogos por sucursal
export const getOdontologosPorSucursal = async (sucId) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await axios.get(`${API_BASE_URL}/odontologo/listaOdontologosSucursal/${sucId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    
    return response.data; // Devolvemos la respuesta completa
  } catch (error) {
    console.error('Error al obtener los odontólogos por sucursal:', error);
    throw error;
  }
};

// Método: Crear un nuevo agendamiento
export const createAgendamiento = async (agendamientoData) => {
  try {
    const token = localStorage.getItem('authToken'); // Obtener el token del localStorage
    if (!token) {
      throw new Error('No se encontró un token de autenticación');
    }

    const response = await axios.post(`${API_BASE_URL}/agenda/nuevo-agendamiento`, agendamientoData, {
      headers: {
        Authorization: `Bearer ${token}`, // Incluir el token en los encabezados
        'Content-Type': 'application/json',
      },
    });

    return response.data; // Devolver la respuesta del backend
  } catch (error) {
    console.error('Error al crear el agendamiento:', error.response?.data || error.message);
    throw error; // Lanzar el error para manejarlo en el componente
  }
};

// Método: Crear un nuevo paciente
export const nuevoPaciente = async (pacienteData) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No se encontró un token de autenticación');
    }

    pacienteData.pacRut = pacienteData.pacRut.toString().replace(/\D/g, ''); // Eliminar caracteres no numéricos
    
    const response = await axios.post(`${API_BASE_URL}/paciente/nuevo-paciente`, pacienteData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error al crear el paciente:', error.response?.data || error.message);
    throw error;
  }
};

// Método: Obtener lista de sucursales
export const getSucursales = async () => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await axios.get(`${API_BASE_URL}/sucursal/listaSucursales`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.datos;
  } catch (error) {
    console.error('Error al obtener la lista de sucursales:', error);
    throw error;
  }
};